import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  MenuItem,
  Select,
  TextField,
  useTheme,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import moment from "moment";
import { enqueueSnackbar } from "notistack";
import React, { useMemo, useState } from "react";
import { Loading } from "../../components";
import CustomDialogTitle from "../../components/CustomDialogTitle";
import {
  allSupportActivities,
  attendeeNameNotRequiredActivities,
  attendeeNameRequiredActivities,
  getStyles,
  radioGroupMenuProps,
} from "../../constants/constants";
import { useMenteeContext } from "../../context/MenteeContext";
import { useUserContext } from "../../context/UserContext";
import { SaveFssSupport, uploadDocument } from "../../utils/services";
import { DropzoneArea } from "mui-file-dropzone";

const FssSupportForm = ({ open, setOpen, onSubmit }) => {
  const { user } = useUserContext();
  const [loading, setLoading] = useState(false);
  const { menteeList } = useMenteeContext();
  const formInitialValues = {
    mentorId: user.id,
    activityType: "",
    activityDate: dayjs(),
    activityDuration: 0,
    menteeIds: [],
    noOfAttendees: "",
  };
  const [formData, setFormData] = useState(formInitialValues);
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState(null);
  const [dropzoneKey, setDropzoneKey] = React.useState(Date.now());
  const fileExtList = [
    "vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "vnd.openxmlformats-officedocument.presentationml.presentation",
    "vnd.openxmlformats-officedocument.wordprocessingml.document",
    "vnd.oasis.opendocument.presentation",
    "vnd.oasis.opendocument.spreadsheet",
    "vnd.oasis.opendocument.text",
    "msword",
    "rfc822",
  ];

  const theme = useTheme();

  const requiresAttendees = useMemo(() => {
    return attendeeNameRequiredActivities.includes(formData.activityType);
  }, [formData.activityType]);

  const handleFileChange = async (files) => {
    if (files.length > 0) {
      const file_Name = files[0]?.name;
      setFile(files[0]);
      setFileName(file_Name);
    } else {
      setFile(null);
      setFileName(null);
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    if (
      name == "activityType" &&
      attendeeNameNotRequiredActivities.includes(value)
    ) {
      setFormData((prevState) => ({
        ...prevState,
        menteeIds: [],
      }));
    }
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleAttendeeChange = (event) => {
    const value = event.target.value;
    const attendeeCount = formData.noOfAttendees;

    // if we already have selected number of attendees and
    // user is trying to add more then don't allow
    if (
      formData.menteeIds.length == attendeeCount &&
      value.length > attendeeCount
    )
      return;

    setFormData((prevState) => ({
      ...prevState,
      menteeIds: value,
    }));
  };

  const clearUploadDocumentData = () => {
    setFile(null); // Clear the uploaded file
    setFileName(null); // Clear the file name
    setDropzoneKey(Date.now());
  };

  const handleClose = () => {
    setOpen(false);
    setFormData(formInitialValues);
    clearUploadDocumentData();
  };

  const getBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!file) return;

    try {
      setLoading(true);
      let substr3 = fileName?.split(".").pop();
      console.log("filename and substrng: ", fileName, substr3);

      var doc = await getBase64(file);
      var base64Data = "";
      console.log("doc length>>>", doc.length, doc);
      var extentionStartIndex = doc.indexOf("/");
      var extensionEndIndex = doc.indexOf(";");
      var fileExtension = doc.substring(
        extentionStartIndex + 1,
        extensionEndIndex
      );
      console.log("Base 64 conertion>>", fileExtension);

      if (fileExtList.includes(fileExtension)) {
        base64Data = doc?.replace(fileExtension, substr3);
      } else {
        base64Data = doc;
      }
      console.log("base64Data is: ", base64Data);

      const documentResponse = await uploadDocument(
        formData.activityType,
        fileName,
        base64Data,
        user.id,
        user.token
      );

      if (documentResponse.status === "success") {
        const parsedData = JSON.parse(documentResponse.data);
        const basicDocId = parsedData[0]?.basicDocId;
        console.log(basicDocId);
        enqueueSnackbar("Document saved successfully!", {
          variant: "success",
        });

        const updatedFormData = {
          ...formData,
          activityDate: moment(formData.activityDate.$d).format("YYYY-MM-DD"),
          menteeIds: requiresAttendees
            ? formData.menteeIds.join(",") // Set menteeIds to null if attendee selection is disabled
            : null,
          docId: basicDocId,
        };

        const response = await SaveFssSupport(user.token, updatedFormData);

        if (response.status !== 200 && response.status !== 202) {
          throw new Error(response.error || "Unknown error occurred");
        }

        enqueueSnackbar("FSS support saved successfully!", {
          variant: "success",
        });
        handleClose();
        onSubmit();
      } else {
        throw new Error("Document upload failed");
      }
    } catch (error) {
      console.log(error);
      enqueueSnackbar("Something went wrong. Please try again later.", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = useMemo(() => {
    if (requiresAttendees) {
      return (
        formData.activityType &&
        formData.activityDate &&
        Number(formData.activityDuration) > 0 &&
        formData.noOfAttendees > 0 &&
        formData.menteeIds.length === Number(formData.noOfAttendees) &&
        file
      );
    } else {
      return (
        formData.activityType &&
        formData.activityDate &&
        Number(formData.activityDuration) > 0 &&
        formData.noOfAttendees > 0 &&
        formData.menteeIds.length == 0 &&
        file
      );
    }
  }, [formData, file]);

  const isWeekend = (date) => {
    const day = date.day();
    return day === 0 || day === 7;
  };

  const endDate = dayjs();
  const startDate = dayjs().subtract(7, "day");

  return (
    <Dialog onClose={handleClose} open={open}>
      <CustomDialogTitle onClose={handleClose}>
        Add FSS Support
      </CustomDialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl size="small" fullWidth>
              <FormLabel>FSS Support</FormLabel>
              <Select
                name="activityType"
                value={formData.activityType}
                onChange={handleInputChange}
              >
                {allSupportActivities.map((option, index) => (
                  <MenuItem key={index} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl size="small" fullWidth>
              <FormLabel>Date of intervention/support</FormLabel>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={formData.activityDate}
                  onChange={(newValue) => {
                    setFormData((prevState) => ({
                      ...prevState,
                      activityDate: newValue,
                    }));
                  }}
                  format="YYYY/MM/DD"
                  disableFuture
                  shouldDisableDate={(date) =>
                    isWeekend(date) ||
                    date.isBefore(startDate) ||
                    date.isAfter(endDate)
                  }
                  maxDate={endDate}
                  minDate={startDate}
                  sx={{ width: "100%" }}
                />
              </LocalizationProvider>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <FormLabel>Duration of intervention</FormLabel>
              <TextField
                type="number"
                name="activityDuration"
                value={formData.activityDuration}
                onChange={(e) => {
                  const value = parseInt(e.target.value, 10);
                  if (value >= 0 || e.target.value === "") {
                    handleInputChange(e);
                  }
                }}
                placeholder="Enter duration in minutes"
                fullWidth
              />
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <FormLabel>Number of Attendees</FormLabel>
              <TextField
                type="number"
                name="noOfAttendees"
                value={formData.noOfAttendees}
                onChange={(e) => {
                  const value = parseInt(e.target.value, 10);
                  if (value >= 0 || e.target.value === "") {
                    handleInputChange(e);
                  }
                }}
                placeholder="Enter the number of attendees"
              />
              {formData.noOfAttendees > menteeList.length && (
                <FormHelperText sx={{ color: "red", marginLeft: 0 }}>
                  The number of attendees cannot exceed the number of assigned
                  facilitator ({menteeList.length}).
                </FormHelperText>
              )}
            </FormControl>
          </Grid>
          {requiresAttendees && (
            <Grid item xs={12}>
              <FormControl fullWidth>
                <FormLabel>Name of Attendees</FormLabel>
                <Select
                  multiple
                  name="menteeIds"
                  value={formData.menteeIds}
                  onChange={handleAttendeeChange}
                  renderValue={(selected) => {
                    // we get the mentee ids of selected mentees in selected variable
                    // to show the name of the mentees filter mentee list
                    const selectedMentees = menteeList.filter((mentee) =>
                      selected.includes(mentee.id)
                    );
                    return (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selectedMentees.map((mentee) => (
                          <Chip key={mentee.id} label={mentee.name} />
                        ))}
                      </Box>
                    );
                  }}
                  MenuProps={radioGroupMenuProps}
                >
                  {menteeList.map((attendee) => (
                    <MenuItem
                      key={attendee.id}
                      value={attendee.id}
                      style={getStyles(attendee.id, formData.menteeIds, theme)}
                    >
                      {attendee.name}
                    </MenuItem>
                  ))}
                </Select>
                {formData.noOfAttendees &&
                  formData.noOfAttendees <= menteeList.length &&
                  formData.noOfAttendees != formData.menteeIds.length && (
                    <FormHelperText sx={{ color: "red", marginLeft: 0 }}>
                      Select {formData.noOfAttendees} attendees.
                    </FormHelperText>
                  )}
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12} sm={6}>
            <DropzoneArea
              name="file"
              id="file"
              acceptedFiles={[
                "image/*",
                ".csv",
                ".png",
                "text/*",
                ".docx",
                ".pdf",
                ".ppt",
                ".pptx",
                ".xls",
                ".xlsx",
                ".txt",
                ".svg",
              ]}
              maxFileSize={5000000}
              filesLimit={1}
              showFileNames={true}
              onChange={handleFileChange}
              key={dropzoneKey}
              onDelete={clearUploadDocumentData}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button variant="contained" onClick={handleClose} color="error">
          Cancel
        </Button>
        <Button
          variant="contained"
          type="submit"
          color="success"
          // disabled={!isFormValid}
          onClick={handleSubmit}
        >
          Save
        </Button>
      </DialogActions>
      <Loading open={loading} />
    </Dialog>
  );
};

export default FssSupportForm;
